import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, object, ref } from 'yup'

import {
  nonEmptyStringSchema,
  stringSchema,
  useMetaResponseHandler,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { UserContract } from '@/User/application'
import { PasswordChangeDataForm, UserRepository } from '@/User/domain'
import { passwordChangerTranslation } from '@/User/ui/userTranslation'

interface IUseChangePassword {
  form: UseFormReturn<PasswordChangeDataForm>
  save: (data: PasswordChangeDataForm) => Promise<boolean>
}

const defaultValues: PasswordChangeDataForm = {
  oldPassword: '',
  newPassword: '',
  newPasswordRepeat: '',
}

const formValidationScheme = (t: TFunction): ObjectSchema<PasswordChangeDataForm> =>
  object({
    oldPassword: nonEmptyStringSchema(t),
    newPassword: nonEmptyStringSchema(t).matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.{8,})/,
      t(formTranslation.errors.passwordFormat),
    ),
    newPasswordRepeat: stringSchema(t)
      .oneOf([ref('newPassword')], t(formTranslation.errors.passwordMatch))
      .defined(),
  })

export const useChangePassword = (repository: UserRepository): IUseChangePassword => {
  const { startLoading, stopLoading } = useLoader()
  const { t } = useTranslation()
  const { handleMetaResponse } = useMetaResponseHandler()

  const form = useForm<PasswordChangeDataForm>({
    resolver: yupResolver(formValidationScheme(t)),
    mode: 'onChange',
    defaultValues,
  })

  const save = async (data: PasswordChangeDataForm): Promise<boolean> => {
    startLoading()

    const response = await UserContract(repository).changePassword(data)

    stopLoading()

    const valid = handleMetaResponse(response?.meta, form, {
      successMessage: t(passwordChangerTranslation.success),
    })

    if (valid) {
      form.reset({
        ...form.getValues(),
        ...defaultValues,
      })
    }

    return valid
  }

  return { form, save }
}
